import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trophy, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import useMarkAsReadOnMount from '../hooks/useMarkAsReadOnMount';
import './Users.css';
import './TasksRewards.css';

const MyRewards = () => {
  useMarkAsReadOnMount(['reward']);

  const { user } = useAuth();
  const now = new Date();
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [myLine, setMyLine] = useState(null);
  const [reportInfo, setReportInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const currentUserId = useMemo(() => Number(user?.id ?? user?.userId ?? 0), [user]);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.users)) return value.users;
    return [];
  };

  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const projectsRes = await api.get('/projects');
      const records = normalizeArray(projectsRes);
      setProjects(records);

      if (!records.length) {
        setSelectedProjectId('');
        setMyLine(null);
        setReportInfo(null);
        setMessage({ type: 'error', text: 'No projects available for rewards reporting.' });
        return;
      }

      setSelectedProjectId((prev) => {
        if (prev && records.some((p) => String(p.id) === String(prev))) return prev;
        return String(records[0].id);
      });
      setMessage({ type: '', text: '' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to load projects.' });
      setMyLine(null);
      setReportInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMyRewardLine = useCallback(async () => {
    if (!selectedProjectId || !currentUserId) {
      setMyLine(null);
      setReportInfo(null);
      return;
    }

    setLoading(true);
    try {
      const data = await api.get(
        `/projects/${selectedProjectId}/monthly-report?month=${Number(month)}&year=${Number(year)}`,
      );

      const contributors = Array.isArray(data?.contributors) ? data.contributors : [];
      const mine = contributors.find((c) => Number(c.userId) === Number(currentUserId)) || null;

      setMyLine(mine);
      setReportInfo({
        projectName: data?.projectName,
        totalRewardPool: Number(data?.totalRewardPool || 0),
      });

      if (!mine) {
        setMessage({ type: 'error', text: 'You are not part of this project for the selected month.' });
      } else {
        setMessage({ type: '', text: '' });
      }
    } catch (err) {
      setMyLine(null);
      setReportInfo(null);
      setMessage({ type: 'error', text: err.message || 'Failed to load your rewards line.' });
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, month, year, currentUserId]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    loadMyRewardLine();
  }, [loadMyRewardLine]);

  const getStatusStyle = (entry) => {
    if (!entry) {
      return {
        background: 'rgba(148, 163, 184, 0.18)',
        color: '#cbd5e1',
        border: '1px solid rgba(148, 163, 184, 0.35)',
        label: 'No Data',
      };
    }

    if (entry.disqualified) {
      return {
        background: 'rgba(239, 68, 68, 0.14)',
        color: '#fca5a5',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        label: 'Disqualified',
      };
    }

    if (entry.attendanceRate >= 95) {
      return {
        background: 'rgba(34, 197, 94, 0.14)',
        color: '#86efac',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        label: 'Excellent',
      };
    }

    if (entry.attendanceRate >= 80) {
      return {
        background: 'rgba(245, 158, 11, 0.14)',
        color: '#fcd34d',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        label: 'Good',
      };
    }

    return {
      background: 'rgba(148, 163, 184, 0.18)',
      color: '#cbd5e1',
      border: '1px solid rgba(148, 163, 184, 0.35)',
      label: 'Normal',
    };
  };

  const status = getStatusStyle(myLine);

  if (loading) return <div className="page-loading">Loading my rewards...</div>;

  return (
    <div className="users-page">
      <div className="page-header">
        <h1><Trophy size={24} /> My Rewards</h1>
        <button className="btn btn-secondary" onClick={loadMyRewardLine}><RefreshCw size={14} /> Refresh</button>
      </div>

      {message.text ? (
        <div className={`message ${message.type}`} style={{ marginBottom: 14 }}>{message.text}</div>
      ) : null}

      <div className="users-table-container tr-card" style={{ marginBottom: 16, padding: 16 }}>
        <h3 className="tr-card-header">Project & Month Filters</h3>
        <div className="users-filters tr-form-grid" style={{ gap: 10, gridTemplateColumns: '2fr 1fr 1fr auto' }}>
          <select
            className="tr-select"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          >
            <option value="">Select project</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select className="tr-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <input
            className="tr-input"
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
          <button className="btn btn-secondary" onClick={loadMyRewardLine}><RefreshCw size={14} /> Run</button>
        </div>
      </div>

      <div className="users-table-container tr-card" style={{ marginBottom: 16, padding: 16 }}>
        <h3 className="tr-card-header">My Monthly Reward Line</h3>
        {myLine ? (
          <div>
            <div className="tr-summary-line" style={{ marginBottom: 12 }}>
              <strong>{reportInfo?.projectName || 'Selected Project'}</strong> · {monthOptions.find((m) => m.value === Number(month))?.label} {year} · Pool {Number(reportInfo?.totalRewardPool || 0).toFixed(2)}
            </div>
            <div className="list-meta" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(140px,1fr))', gap: 12 }}>
              <div><strong>Weighted Done</strong><div>{Number(myLine.weightedCompleted || 0).toFixed(2)}</div></div>
              <div><strong>Quota</strong><div>{Number(myLine.quota || 0).toFixed(2)}</div></div>
              <div><strong>Over Quota</strong><div>{Number(myLine.overQuotaScore || 0).toFixed(2)}</div></div>
              <div><strong>Attendance</strong><div>{Number(myLine.attendanceRate || 0).toFixed(2)}%</div></div>
              <div><strong>Multiplier</strong><div>{Number(myLine.multiplier || 0).toFixed(2)}x</div></div>
              <div><strong>Final Score</strong><div>{Number(myLine.finalScore || 0).toFixed(2)}</div></div>
              <div><strong>Reward</strong><div>{Number(myLine.estimatedReward || 0).toFixed(2)}</div></div>
              <div>
                <strong>Status</strong>
                <div style={{ marginTop: 4 }}>
                  <span
                    style={{
                      ...status,
                      borderRadius: 999,
                      padding: '2px 8px',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {status.label}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-row">No personal reward line found for selected project/month.</div>
        )}
      </div>
    </div>
  );
};

export default MyRewards;
